import { useTranslation } from 'react-i18next'
import type { LocalizedString, LocalizedStringArray } from '@/types/nf'

export function resolveLocalized(v: LocalizedString | string | undefined | null, lang: string): string {
  if (typeof v === 'string') return v
  if (!v) return ''

  // 后端按 D1-a 不透明透传 payload(只校验是 JSON object, 不校验内部字段类型);
  // 自定义模板可能存入非 string 值(如 {"genre_label":{"zh":123,"en":null}})。
  // resolver 是"不可信数据 → 类型化 string"的信任边界, 必须守卫非 string 防 .trim() 崩溃。
  const pick = (x: unknown): string => (typeof x === 'string' ? x.trim() : '')

  const requested = pick(v[lang])
  if (requested) return requested

  const english = pick(v.en)
  if (english) return english

  for (const value of Object.values(v)) {
    const s = pick(value)
    if (s) return s
  }
  return ''
}

export function resolveLocalizedArray(v: LocalizedStringArray | string[] | undefined | null, lang: string): string[] {
  if (Array.isArray(v)) return v.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
  if (!v) return []

  const pick = (x: unknown): string[] =>
    Array.isArray(x) ? x.filter((item): item is string => typeof item === 'string' && item.trim() !== '') : []

  const requested = pick(v[lang])
  if (requested.length > 0) return requested

  const english = pick(v.en)
  if (english.length > 0) return english

  for (const value of Object.values(v)) {
    const items = pick(value)
    if (items.length > 0) return items
  }
  return []
}

export function useLocalized() {
  const { i18n } = useTranslation()
  const lang = i18n.resolvedLanguage ?? i18n.language

  return (v: LocalizedString | string | undefined | null) => resolveLocalized(v, lang)
}

export function useLocalizedArray() {
  const { i18n } = useTranslation()
  const lang = i18n.resolvedLanguage ?? i18n.language

  return (v: LocalizedStringArray | string[] | undefined | null) => resolveLocalizedArray(v, lang)
}
